import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Share2, X } from "lucide-react";
import { api } from "@/lib/api";
import OnePagerDokument, { MODES } from "./OnePagerDokument";
import "./onepager.css";

const MODE_NAVN = { general: "General", speaker: "Speaker", partner: "Partner" };

/* Del-modal — én stabil, passordbeskyttet lenke per modus */
function DelModal({ onLukk }) {
  const [links, setLinks] = useState(null);
  const [feilet, setFeilet] = useState(false);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    api.get("/admin/os/one-pager/share")
      .then((r) => setLinks(r.data.links))
      .catch(() => setFeilet(true));
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const kopier = (tekst, hva) => {
    navigator.clipboard?.writeText(tekst).then(
      () => toast.success(`${hva} kopiert`),
      () => toast.error("Kunne ikke kopiere")
    );
  };

  const toggle = async (l) => {
    try {
      const r = await api.patch(`/admin/os/one-pager/share/${l.mode}`, { active: !l.active });
      setLinks((ls) => ls.map((x) => (x.mode === l.mode ? r.data.link : x)));
      toast.success(r.data.link.active ? "Lenken er aktivert" : "Lenken er deaktivert");
    } catch { toast.error("Noe gikk galt"); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[10vh] px-4 op-chrome" data-testid="op-del-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[4px]" onClick={onLukk} />
      <div className="relative w-full max-w-[600px] bg-[#0e1318] border border-white/[0.1] rounded-[4px] p-6 text-left">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-[15px] font-semibold text-[#f5f1e8]">Del one-pager</h2>
          <button type="button" onClick={onLukk} className="text-[#8b9199] hover:text-white transition-colors" data-testid="op-del-lukk">
            <X size={16} />
          </button>
        </div>
        <p className="text-[12px] text-[#8b9199] mb-4">
          Passordbeskyttede lenker — én per brief. Mottakeren ser kun sin variant. Del lenken og koden hver for seg.
        </p>
        {feilet && <p className="text-[12.5px] text-red-400">Kunne ikke hente lenkene.</p>}
        {!feilet && !links && <p className="text-[12.5px] text-[#565c66] adm-puls">Henter lenker …</p>}
        {links?.map((l) => {
          const url = `${window.location.origin}/one-pager/s/${l.token}`;
          return (
            <div key={l.mode} className="py-4 border-t border-white/[0.07] first:border-t-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[12px] font-semibold text-[#f5f1e8]">{MODE_NAVN[l.mode]}</p>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-[9px] uppercase tracking-[0.16em] ${l.active ? "text-emerald-400" : "text-red-400"}`}>
                    {l.active ? "Aktiv" : "Deaktivert"}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggle(l)}
                    className="text-[10.5px] text-[#8b9199] hover:text-white border border-white/[0.14] hover:border-white/[0.3] rounded-[2px] px-2.5 py-1 transition-colors"
                    data-testid={`op-del-toggle-${l.mode}`}
                  >
                    {l.active ? "Deaktiver" : "Aktiver"}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-[11px] text-[#b7bdc5] border border-white/[0.1] rounded-[2px] px-2.5 py-2" data-testid={`op-del-url-${l.mode}`}>{url}</code>
                <button type="button" onClick={() => kopier(url, "Lenken")} className="shrink-0 p-2 text-[#8b9199] hover:text-white border border-white/[0.14] hover:border-white/[0.3] rounded-[2px] transition-colors" data-testid={`op-del-kopier-url-${l.mode}`}>
                  <Copy size={12} />
                </button>
                <code className="shrink-0 font-mono text-[12.5px] tracking-[0.28em] text-[#f5f1e8] border border-white/[0.1] rounded-[2px] px-3 py-2" data-testid={`op-del-kode-${l.mode}`}>{l.passcode}</code>
                <button type="button" onClick={() => kopier(l.passcode, "Koden")} className="shrink-0 p-2 text-[#8b9199] hover:text-white border border-white/[0.14] hover:border-white/[0.3] rounded-[2px] transition-colors" data-testid={`op-del-kopier-kode-${l.mode}`}>
                  <Copy size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OnePager() {
  const { mode: modeParam } = useParams();
  const mode = MODES[modeParam] ? modeParam : "general";
  const [ev, setEv] = useState(null);
  const [erAdmin, setErAdmin] = useState(false);
  const [delModal, setDelModal] = useState(false);

  useEffect(() => {
    let aktiv = true;
    api.get("/one-pager").then((r) => { if (aktiv) setEv(r.data.event || null); }).catch(() => {});
    api.get("/admin/me").then(() => { if (aktiv) setErAdmin(true); }).catch(() => {});
    return () => { aktiv = false; };
  }, []);

  const chrome = (
    <>
      <nav className="op-modes op-chrome" data-testid="onepager-modes">
        <Link to="/vision/one-pager" className={mode === "general" ? "aktiv" : ""} data-testid="onepager-mode-general">General</Link>
        <Link to="/vision/one-pager/speaker" className={mode === "speaker" ? "aktiv" : ""} data-testid="onepager-mode-speaker">Speaker</Link>
        <Link to="/vision/one-pager/partner" className={mode === "partner" ? "aktiv" : ""} data-testid="onepager-mode-partner">Partner</Link>
      </nav>
      <div className="op-tools op-chrome">
        {erAdmin && (
          <button type="button" className="op-pdf" onClick={() => setDelModal(true)} data-testid="op-del-btn">
            <Share2 size={11} /> Del
          </button>
        )}
        <button type="button" className="op-pdf" onClick={() => window.print()} data-testid="onepager-pdf-btn">
          Print / PDF
        </button>
      </div>
      {delModal && <DelModal onLukk={() => setDelModal(false)} />}
    </>
  );

  return <OnePagerDokument mode={mode} ev={ev} chrome={chrome} />;
}
